import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import './Invoices.css';

function Invoices() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    role: user?.userType === 'seller' ? 'seller' : 'buyer'
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [filters, page]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(filters.status && { status: filters.status }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
      });

      const response = await api.get(`/invoices?${params}`);

      setInvoices(response.data.invoices);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get(`/invoices/stats/summary?role=${filters.role}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/invoices/${invoiceId}`);
  };

  const handleGenerateInvoice = () => {
    navigate('/orders');
    toast.info('Select an order to generate invoice');
  };

  return (
    <Layout>
      <div className="invoices-page">
        <div className="page-header">
          <h1>Invoices</h1>
          {user?.userType === 'seller' && (
            <button className="btn btn-primary" onClick={handleGenerateInvoice}>
              + Generate Invoice
            </button>
          )}
        </div>

        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <div className="stat-value">{stats.totalInvoices}</div>
                <div className="stat-label">Total Invoices</div>
              </div>
            </div>

            <div className="stat-card success">
              <div className="stat-icon">✅</div>
              <div className="stat-info">
                <div className="stat-value">₹{stats.paidAmount.toFixed(2)}</div>
                <div className="stat-label">Paid ({stats.paidInvoices})</div>
              </div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon">⏳</div>
              <div className="stat-info">
                <div className="stat-value">₹{stats.unpaidAmount.toFixed(2)}</div>
                <div className="stat-label">Unpaid ({stats.unpaidInvoices})</div>
              </div>
            </div>

            <div className="stat-card danger">
              <div className="stat-icon">⚠️</div>
              <div className="stat-info">
                <div className="stat-value">{stats.overdueInvoices}</div>
                <div className="stat-label">Overdue</div>
              </div>
            </div>
          </div>
        )}

        <div className="filters-section">
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="viewed">Viewed</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="filter-group">
            <label>From:</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>To:</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <button
            className="btn btn-secondary"
            onClick={() => {
              setFilters({
                status: '',
                startDate: '',
                endDate: '',
                role: filters.role
              });
              setPage(1);
            }}
          >
            Clear Filters
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading invoices...</div>
        ) : invoices.length === 0 ? (
          <div className="no-invoices">
            <p>No invoices found</p>
            {user?.userType === 'seller' && (
              <button className="btn btn-primary" onClick={handleGenerateInvoice}>
                Generate Your First Invoice
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="invoices-table">
              <table>
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Due Date</th>
                    <th>{user?.userType === 'seller' ? 'Buyer' : 'Seller'}</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice._id}>
                      <td className="invoice-number">{invoice.invoiceNumber}</td>
                      <td>{new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                      <td>{new Date(invoice.dueDate).toLocaleDateString()}</td>
                      <td>
                        {user?.userType === 'seller'
                          ? invoice.buyerDetails.name || invoice.buyerDetails.email
                          : invoice.sellerDetails.businessName || invoice.sellerDetails.email}
                      </td>
                      <td className="amount">₹{invoice.summary.grandTotal.toFixed(2)}</td>
                      <td>
                        <span className={`status-badge ${invoice.status}`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-icon"
                          onClick={() => handleViewInvoice(invoice._id)}
                          title="View Invoice"
                        >
                          👁️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default Invoices;
